import React, { useState, useEffect } from 'react';
import { Plus, Settings, Eye, EyeOff, Copy, MoreHorizontal, Edit2, RefreshCw, Trash2, FileCode, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogTrigger } from '../components/ui/dialog';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import { useToast } from '../components/ui/toast';
import Layout from '../components/Layout';
import { appsAPI, UserApp } from '../services/api';
import CreateAppDialog from '../components/CreateAppDialog';
import EditAppDialog from '../components/EditAppDialog';
import DeleteAppDialog from '../components/DeleteAppDialog';
import RegenerateSecretDialog from '../components/RegenerateSecretDialog';

const Apps = () => {
  const navigate = useNavigate();
  const [apps, setApps] = useState<UserApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingApp, setEditingApp] = useState<UserApp | null>(null);
  const [deletingApp, setDeletingApp] = useState<UserApp | null>(null);
  const [regeneratingApp, setRegeneratingApp] = useState<UserApp | null>(null);
  const [visibleSecrets, setVisibleSecrets] = useState<Set<string>>(new Set());

  const { addToast } = useToast();

  useEffect(() => {
    fetchApps();
  }, []);

  const fetchApps = async () => {
    try {
      setLoading(true);
      const response = await appsAPI.list();
      setApps(response.data.data);
    } catch (error) {
      console.error('Failed to fetch apps:', error);
      addToast({
        title: "Error",
        description: "Failed to load applications. Please refresh the page.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateApp = async (data: { name: string; description?: string }) => {
    try {
      const response = await appsAPI.create(data);
      setApps([response.data.data, ...apps]);
      setShowCreateDialog(false);
      addToast({
        title: "App Created",
        description: "Application created successfully.",
        variant: "success"
      });
    } catch (error) {
      console.error('Failed to create app:', error);
      addToast({
        title: "Creation Failed",
        description: "Failed to create application. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleUpdateApp = async (appId: string, data: { name: string; description?: string }) => {
    try {
      const response = await appsAPI.update(appId, data);
      setApps(apps.map(app => app.id === appId ? response.data.data : app));
      setEditingApp(null);
      addToast({
        title: "App Updated",
        description: "Application updated successfully.",
        variant: "success"
      });
    } catch (error) {
      console.error('Failed to update app:', error);
      addToast({
        title: "Update Failed",
        description: "Failed to update application. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleDeleteApp = async (appId: string) => {
    try {
      await appsAPI.delete(appId);
      setApps(apps.filter(app => app.id !== appId));
      setDeletingApp(null);
      addToast({
        title: "App Deleted",
        description: "Application deleted successfully.",
        variant: "success"
      });
    } catch (error) {
      console.error('Failed to delete app:', error);
      addToast({
        title: "Delete Failed",
        description: "Failed to delete application. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleRegenerateSecret = async (appId: string) => {
    try {
      const response = await appsAPI.regenerateSecret(appId);
      setApps(apps.map(app => app.id === appId ? response.data.data : app));
      setRegeneratingApp(null);
      addToast({
        title: "Secret Regenerated",
        description: "Secret key regenerated successfully.",
        variant: "success"
      });
    } catch (error) {
      console.error('Failed to regenerate secret:', error);
      addToast({
        title: "Regeneration Failed",
        description: "Failed to regenerate secret key. Please try again.",
        variant: "destructive"
      });
    }
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      addToast({
        title: "Copied",
        description: `${label} copied to clipboard.`,
        variant: "success"
      });
    } catch (error) {
      addToast({
        title: "Copy Failed",
        description: "Failed to copy to clipboard. Please try again.",
        variant: "destructive"
      });
    }
  };

  const toggleSecretVisibility = (appId: string) => {
    const newVisible = new Set(visibleSecrets);
    if (newVisible.has(appId)) {
      newVisible.delete(appId);
    } else {
      newVisible.add(appId);
    }
    setVisibleSecrets(newVisible);
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString));
  };

  const maskSecret = (secret: string) => {
    return secret.slice(0, 8) + '•'.repeat(Math.max(0, secret.length - 16)) + secret.slice(-8);
  };

  return (
    <Layout 
      title="Applications" 
      description="Manage your API applications and credentials"
    >
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="max-w-5xl mx-auto space-y-4 sm:space-y-6">
          {/* Create App Action */}
          <div className="flex justify-end items-center">
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger 
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
                onClick={() => setShowCreateDialog(true)}
              >
                <Plus className="h-4 w-4" />
                Create App
              </DialogTrigger>
            </Dialog>
          </div>

          {/* Apps List */}
          <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                <p className="text-sm text-muted-foreground">Loading applications...</p>
              </div>
            ) : apps.length === 0 ? (
              <div className="text-center py-12 px-4">
                <div className="h-16 w-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Settings className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium mb-2">No applications found</h3>
                <p className="text-muted-foreground mb-4">
                  Create your first application to start using the API
                </p>
                <Button onClick={() => setShowCreateDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create App
                </Button>
              </div>
            ) : (
              <>
                {/* Card View for All Screens */}
                <div className="divide-y divide-border">
                  {apps.map((app) => (
                    <div key={app.id} className="p-5 sm:p-6 space-y-5">
                      {/* Header with Name and Mobile Actions */}
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-lg truncate">{app.name}</h3>
                          {app.description && (
                            <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                              {app.description}
                            </p>
                          )}
                        </div>
                        {/* Mobile Dropdown - Hidden on Desktop */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-9 w-9 p-0 touch-target flex-shrink-0 md:hidden"
                            >
                              <MoreHorizontal className="h-5 w-5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => navigate(`/dashboard/apps/${app.id}/api-docs`)}
                            >
                              <FileCode className="h-4 w-4 mr-2" />
                              Test Public APIs
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setEditingApp(app)}
                            >
                              <Edit2 className="h-4 w-4 mr-2" />
                              Edit Details
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => setRegeneratingApp(app)}
                            >
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Regenerate Secret
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setDeletingApp(app)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete App
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {/* App ID */}
                      <div className="space-y-2">
                        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          App ID
                        </label>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 text-xs font-mono bg-muted px-3 py-2.5 rounded-md break-all border">
                            {app.id}
                          </code>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyToClipboard(app.id, 'App ID')}
                            className="h-9 w-9 p-0 touch-target flex-shrink-0 hover:bg-muted"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Secret Key */}
                      <div className="space-y-2">
                        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          Secret Key
                        </label>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 text-xs font-mono bg-muted px-3 py-2.5 rounded-md break-all border">
                            {visibleSecrets.has(app.id) ? app.secret_key : '•'.repeat(32)}
                          </code>
                          <div className="flex gap-1 flex-shrink-0">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => toggleSecretVisibility(app.id)}
                              className="h-9 w-9 p-0 touch-target hover:bg-muted"
                            >
                              {visibleSecrets.has(app.id) ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => copyToClipboard(app.secret_key, 'Secret Key')}
                              className="h-9 w-9 p-0 touch-target hover:bg-muted"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Created Date */}
                      <div className="text-xs text-muted-foreground pt-1">
                        Created {formatDate(app.created_at)}
                      </div>

                      {/* Desktop Action Buttons - Hidden on Mobile */}
                      <div className="hidden md:flex items-center gap-2 pt-4 border-t">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/dashboard/apps/${app.id}/api-docs`)}
                          className="flex-1"
                        >
                          <FileCode className="h-4 w-4 mr-2" />
                          Test APIs
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingApp(app)}
                        >
                          <Edit2 className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setRegeneratingApp(app)}
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Regenerate
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDeletingApp(app)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
        </div>
      </div>

      {/* Dialogs */}
      <CreateAppDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSubmit={handleCreateApp}
      />

      {editingApp && (
        <EditAppDialog
          open={!!editingApp}
          onOpenChange={() => setEditingApp(null)}
          app={editingApp}
          onSubmit={(data) => handleUpdateApp(editingApp.id, data)}
        />
      )}

      {deletingApp && (
        <DeleteAppDialog
          open={!!deletingApp}
          onOpenChange={() => setDeletingApp(null)}
          app={deletingApp}
          onConfirm={() => handleDeleteApp(deletingApp.id)}
        />
      )}

      {regeneratingApp && (
        <RegenerateSecretDialog
          open={!!regeneratingApp}
          onOpenChange={() => setRegeneratingApp(null)}
          app={regeneratingApp}
          onConfirm={() => handleRegenerateSecret(regeneratingApp.id)}
        />
      )}
    </Layout>
  );
};

export default Apps;
